/**
 * 数据契约：ExperimentPack。对应 tech/108 §6。
 *
 * 字段设计上不排斥电学等其他学科：`PhysicsSubject` 预留枚举，`StageDefinition` 不含任何力学专属
 * 必填字段，未来接入电学垂直切片时复用同一份类型，不需要改这里的既有字段。
 */

import type { Variable } from "../eom/variable.js";

export const EXPERIMENT_PACK_SCHEMA_VERSION = "physics.experiment.v1";

export type LabMode = "learn" | "practice" | "exam";
export const LAB_MODES: readonly LabMode[] = ["learn", "practice", "exam"] as const;

export type PhysicsSubject = "mechanics" | "electricity" | "thermal" | "optics";
export const PHYSICS_SUBJECTS: readonly PhysicsSubject[] = [
  "mechanics",
  "electricity",
  "thermal",
  "optics",
] as const;

/**
 * 步骤种类，按 000 文档 §二 的学习链顺序：guess(①猜想)→design(②设计)→act(③动手)→record(④记录)
 * →pattern(⑤发现规律)→knowledge(⑥形成知识点)→conclude(⑦我的结论)→exam(⑧中考挑战)。
 * summary 是章节末的「知识体系总结」Stage 专用，不在这条单实验链里。
 */
export type StepKind =
  | "guess"
  | "design"
  | "act"
  | "record"
  | "pattern"
  | "knowledge"
  | "conclude"
  | "exam"
  | "summary";
export const STEP_KINDS: readonly StepKind[] = [
  "guess",
  "design",
  "act",
  "record",
  "pattern",
  "knowledge",
  "conclude",
  "exam",
  "summary",
] as const;

/** Stage 的种类：`experiment` = 普通子实验（默认）；`summary` = 章节末的「总结 + 综合中考」。 */
export type StageKind = "experiment" | "summary";
export const STAGE_KINDS: readonly StageKind[] = ["experiment", "summary"] as const;

export interface ChoiceOption {
  id: string;
  label: string;
  /** true/false=有明确对错；null/省略=开放性回答（如"我还不确定"），不算错也不算对。 */
  correct?: boolean | null;
  /**
   * design 步骤专用：本选项对应的"改变谁/保持谁不变"方案。
   * 有此字段时，正确性由 rule-engine 现场比对 StageDefinition.independentVar/controlledVars 计算，
   * 忽略 `correct` 字段——避免作者在每个选项上手填 correct 与 Stage 声明不一致。
   */
  variableSelection?: { changed: string; controlled: string[] };
}

export interface ExamQuestion {
  id: string;
  prompt: string;
  options: ChoiceOption[];
}

/** summary 步骤用：一条知识体系总结（标题 + 若干要点）。 */
export interface SummaryItem {
  title: string;
  points: string[];
}

export interface StepDefinition {
  id: string;
  kind: StepKind;
  title: string;
  prompt: string;
  /** guess / design / pattern / conclude 用：单选项列表。 */
  options?: ChoiceOption[];
  /** act 用：必须发生过的 actionId 列表，全部发生才算完成本步骤。 */
  requiredActions?: string[];
  /** record 用：把这些 variable 的当前值快照进数据表的一行。 */
  recordFields?: string[];
  /** record 用：数据表至少要有几行才算完成本步骤，默认 1。 */
  requiredTrials?: number;
  /**
   * conclude 用：区分"你发现了什么"（pattern，对应规律）与"最终结论"（final），
   * 决定上报 `pattern.selected` 还是 `conclusion.submitted`（见 tech/108 §8.2）。默认 "final"。
   */
  conclusionKind?: "pattern" | "final";
  /** 触发的知识卡 id 列表；knowledge 步骤用它指定要展示的知识卡（取第一张）。 */
  knowledgeRefs?: string[];
  /** summary 用：知识体系总结条目，逐条渲染成标题 + 要点列表。 */
  summaryItems?: SummaryItem[];
  /**
   * 中考题。有两种用法：
   * - `conclude` 步骤：附加中考题，跟在本步骤之后可选出现（对齐 000 文档"中考题跟着实验自动出现"），
   *   由 `submitExam()` 作答但**不推进状态机**。
   * - `exam` 步骤：章节末综合中考题，是独立一步，作答后推进状态机。
   */
  exam?: ExamQuestion;
}

export interface StageDefinition {
  id: string;
  title: string;
  /** 本 Stage 的种类：`experiment`（默认）或 `summary`（章节末总结 + 综合中考）。 */
  kind?: StageKind;
  /** 页面顶部的研究问题，如"压力变大，摩擦力会怎样？"。summary stage 用章节级收束问题。 */
  question: string;
  /** 本 Stage 要研究的自变量 id（控制变量法判定用）。 */
  independentVar?: string;
  /** 必须保持不变的变量 id 列表。 */
  controlledVars?: string[];
  steps: StepDefinition[];
}

export interface MissionDefinition {
  schemaVersion: string;
  id: string;
  title: string;
  grade?: string;
  /** 初始变量定义（含默认值），引擎 load() 时写入 PhysicsStore.variables。 */
  variables: Variable[];
  stages: StageDefinition[];
}

export interface ExamPointRef {
  id: string;
  description: string;
  evidence: string[];
}

export interface ExperimentMeta {
  schemaVersion: string;
  id: string;
  subject: PhysicsSubject;
  title: string;
  grade?: string;
  estimatedMinutes?: number;
  examPoints?: ExamPointRef[];
}

export interface KnowledgeItem {
  id: string;
  title: string;
  body: string;
}

export interface AssessmentConfig {
  schemaVersion: string;
}

export interface ExperimentPack {
  experiment: ExperimentMeta;
  mission: MissionDefinition;
  rules: unknown[];
  knowledge: KnowledgeItem[];
  assessment: AssessmentConfig;
}
